import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Rnd } from "react-rnd";
import html2canvas from "html2canvas";
import { Type, Trash2, ImagePlus, CheckCircle2 } from "lucide-react";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const FONTS = ["Poppins", "Georgia", "Impact", "Courier New", "Pacifico", "Brush Script MT"];
const SHIRT_COLORS = [
  { name: "White", hex: "#f4f4f5" }, { name: "Black", hex: "#1a1a1a" }, { name: "Navy", hex: "#0c2340" },
  { name: "Royal Blue", hex: "#1044a5" }, { name: "Gray", hex: "#9ca3af" }, { name: "Red", hex: "#b91c1c" },
];
const SIZES = ["S", "M", "L", "XL", "2XL", "3XL"];
const CANVAS_W = 380, CANVAS_H = 460;

function ShirtBg({ color }) {
  return (
    <svg viewBox="0 0 380 460" className="absolute inset-0 w-full h-full" aria-hidden="true">
      <path d="M110 30 L150 15 Q190 55 230 15 L270 30 L360 90 L320 150 L290 130 L290 445 L90 445 L90 130 L60 150 L20 90 Z"
        fill={color} stroke="#00000022" strokeWidth="2" />
    </svg>
  );
}

export default function VisualStudio() {
  const { user } = useAuth();
  const [side, setSide] = useState("front");
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [shirtColor, setShirtColor] = useState(SHIRT_COLORS[0]);
  const frontRef = useRef(null);
  const backRef = useRef(null);

  const [form, setForm] = useState({ full_name: "", email: "", phone: "", shipping_address: "", size: "M", color: "White", quantity: 1, notes: "", rights_confirmed: false });

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, full_name: user.name || "", email: user.email || "", phone: user.phone || "", shipping_address: user.address || "" }));
  }, [user]);
  useEffect(() => { setForm((f) => ({ ...f, color: shirtColor.name })); }, [shirtColor]);

  const selected = elements.find((e) => e.id === selectedId);

  const addImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/custom-studio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      const id = crypto.randomUUID();
      setElements((p) => [...p, { id, side, type: "image", url, x: 120, y: 150, width: 140, height: 140, rotation: 0 }]);
      setSelectedId(id);
      toast.success("Photo added — drag & resize it on the shirt");
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const addText = () => {
    const id = crypto.randomUUID();
    setElements((p) => [...p, { id, side, type: "text", text: "Your Text", x: 90, y: 200, width: 200, height: 60, rotation: 0, font_family: "Poppins", color: "#0c2340", font_size: 28 }]);
    setSelectedId(id);
  };
  const updateEl = (id, patch) => setElements((p) => p.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const removeEl = (id) => { setElements((p) => p.filter((e) => e.id !== id)); setSelectedId(null); };

  const captureSide = async (ref) => {
    if (!ref.current) return null;
    try {
      const canvas = await html2canvas(ref.current, { backgroundColor: null, useCORS: true, scale: 2, logging: false });
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      if (!blob) return null;
      const fd = new FormData(); fd.append("file", new File([blob], "design.png", { type: "image/png" }));
      const { data } = await api.post("/custom-studio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
    } catch { return null; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rights_confirmed) { toast.error("Please confirm you have rights to any uploaded images."); return; }
    setSubmitting(true);
    try {
      const front_snapshot = await captureSide(frontRef);
      const back_snapshot = await captureSide(backRef);
      const payload = {
        ...form, quantity: Number(form.quantity),
        elements: elements.map((el) => ({ side: el.side, type: el.type, url: el.url || null, text: el.text || null, x: el.x, y: el.y, width: el.width, height: el.height, rotation: el.rotation || 0, font_family: el.font_family || null, color: el.color || null, font_size: el.font_size || null })),
        front_snapshot, back_snapshot,
      };
      await api.post("/custom-studio/orders", payload);
      setDone(true);
      toast.success("Custom order submitted!");
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Something went wrong"); }
    finally { setSubmitting(false); }
  };

  const renderCanvas = (canvasSide, ref, interactive) => (
    <div ref={ref} className="relative rounded-xl overflow-hidden" style={{ width: CANVAS_W, height: CANVAS_H, background: "#eef2f7" }}>
      <ShirtBg color={shirtColor.hex} />
      <div className="absolute left-1/2 top-6 -translate-x-1/2 text-[10px] uppercase tracking-widest text-black/30 font-bold">{canvasSide}</div>
      {elements.filter((el) => el.side === canvasSide).map((el) => (
        interactive ? (
          <Rnd key={el.id} size={{ width: el.width, height: el.height }} position={{ x: el.x, y: el.y }} bounds="parent"
            onDragStop={(_, d) => updateEl(el.id, { x: d.x, y: d.y })}
            onResizeStop={(_, __, r, ___, pos) => updateEl(el.id, { width: parseFloat(r.style.width), height: parseFloat(r.style.height), x: pos.x, y: pos.y })}
            onMouseDown={() => setSelectedId(el.id)}
            className={`${selectedId === el.id ? "ring-2 ring-navy-royal" : ""}`}
            data-testid={`el-${el.id}`}>
            {el.type === "image"
              ? <img src={el.url} alt="" className="w-full h-full object-contain pointer-events-none select-none" crossOrigin="anonymous" />
              : <div className="w-full h-full flex items-center justify-center text-center leading-tight break-words" style={{ fontFamily: el.font_family, color: el.color, fontSize: el.font_size }}>{el.text}</div>}
          </Rnd>
        ) : (
          <div key={el.id} className="absolute" style={{ left: el.x, top: el.y, width: el.width, height: el.height }}>
            {el.type === "image"
              ? <img src={el.url} alt="" className="w-full h-full object-contain" crossOrigin="anonymous" />
              : <div className="w-full h-full flex items-center justify-center text-center leading-tight break-words" style={{ fontFamily: el.font_family, color: el.color, fontSize: el.font_size }}>{el.text}</div>}
          </div>
        )
      ))}
    </div>
  );

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="studio-success">
        <CheckCircle2 size={56} className="mx-auto text-navy-royal" />
        <h1 className="font-serif text-4xl text-navy-dark mt-4">Design submitted!</h1>
        <p className="text-navy/70 mt-3">Thanks {form.full_name?.split(" ")[0]}! We've received your custom design and emailed you a confirmation. Our team will review it and send a secure payment link with your quote. You can track it in your account.</p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/account" className="rounded-md bg-navy-dark text-white px-6 py-3 text-xs font-bold uppercase tracking-[0.15em]">View My Account</Link>
          <Link to="/shop" className="rounded-md border border-navy-dark text-navy-dark px-6 py-3 text-xs font-bold uppercase tracking-[0.15em]">Back to Shop</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="visual-studio">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-script text-3xl text-navy-royal">Design your story</div>
          <h1 className="font-serif text-4xl text-navy-dark">Visual Design Studio</h1>
        </div>
        <div className="inline-flex rounded-full bg-slate-100 p-1 text-sm font-semibold">
          <button onClick={() => setSide("front")} className={`px-5 py-2 rounded-full ${side === "front" ? "bg-navy-dark text-white" : "text-navy/60"}`} data-testid="side-front">Front</button>
          <button onClick={() => setSide("back")} className={`px-5 py-2 rounded-full ${side === "back" ? "bg-navy-dark text-white" : "text-navy/60"}`} data-testid="side-back">Back</button>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-[1fr_320px] gap-8">
        {/* Canvas area */}
        <div className="flex flex-col items-center">
          <div className="relative">
            {renderCanvas(side, side === "front" ? frontRef : backRef, true)}
          </div>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            <label className="inline-flex items-center gap-2 rounded-md bg-navy-dark text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide cursor-pointer hover:bg-navy-royal transition-colors" data-testid="studio-upload-label">
              <ImagePlus size={15} /> {uploading ? "Uploading…" : "Add Photo"}
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={addImage} disabled={uploading} className="hidden" data-testid="studio-upload-input" />
            </label>
            <button onClick={addText} className="inline-flex items-center gap-2 rounded-md border border-navy-dark text-navy-dark px-4 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-navy-dark hover:text-white transition-colors" data-testid="studio-add-text"><Type size={15} /> Add Text</button>
            {selected && <button onClick={() => removeEl(selected.id)} className="inline-flex items-center gap-2 rounded-md border border-cpr text-cpr px-4 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-cpr hover:text-white transition-colors" data-testid="studio-delete-el"><Trash2 size={15} /> Delete</button>}
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-6">
          <div className="rounded-2xl bg-white border border-silver-light p-5">
            <div className="text-xs uppercase tracking-widest text-navy/50 font-semibold">Shirt Color</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {SHIRT_COLORS.map((c) => (
                <button key={c.name} onClick={() => setShirtColor(c)} title={c.name} data-testid={`color-${c.name}`}
                  className={`w-9 h-9 rounded-full border-2 ${shirtColor.name === c.name ? "border-navy-royal ring-2 ring-navy-royal/30" : "border-silver-light"}`} style={{ background: c.hex }} />
              ))}
            </div>
          </div>

          {selected && selected.type === "text" && (
            <div className="rounded-2xl bg-white border border-silver-light p-5 space-y-3" data-testid="text-controls">
              <div className="text-xs uppercase tracking-widest text-navy/50 font-semibold">Text</div>
              <Input value={selected.text} onChange={(e) => updateEl(selected.id, { text: e.target.value })} data-testid="text-value" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Font</Label>
                  <select value={selected.font_family} onChange={(e) => updateEl(selected.id, { font_family: e.target.value })} className="w-full h-9 rounded-md border border-input px-2 text-sm" data-testid="text-font">
                    {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Size</Label>
                  <Input type="number" min={10} max={80} value={selected.font_size} onChange={(e) => updateEl(selected.id, { font_size: Number(e.target.value) })} className="h-9" data-testid="text-size" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Color</Label>
                <input type="color" value={selected.color} onChange={(e) => updateEl(selected.id, { color: e.target.value })} className="w-10 h-8 rounded" data-testid="text-color" />
              </div>
            </div>
          )}

          <div className="rounded-2xl bg-slate-50 border border-silver-light p-5 text-sm text-navy/70">
            <div className="font-semibold text-navy-dark mb-1">How it works</div>
            Add photos & text, drag to position, and resize from the corners. Switch Front/Back to design both sides, then continue to your order.
          </div>

          <Button onClick={() => setShowForm(true)} className="w-full bg-navy-dark hover:bg-navy-royal text-white rounded-md py-6 uppercase tracking-[0.15em] text-xs font-bold" data-testid="studio-continue">
            Continue to Order Request
          </Button>
        </div>
      </div>

      {/* Off-screen render of both sides for snapshot */}
      <div style={{ position: "absolute", left: -99999, top: 0 }} aria-hidden="true">
        <div ref={side === "front" ? backRef : frontRef}>{renderCanvas(side === "front" ? "back" : "front", { current: null }, false)}</div>
      </div>

      {/* Order form */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !submitting && setShowForm(false)} data-testid="studio-form-overlay">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-2xl text-navy-dark">Order Request</h2>
            <p className="text-sm text-navy/60 mt-1">Review your details and submit — we'll send a quote & payment link.</p>
            <form onSubmit={submit} className="mt-4 space-y-3" data-testid="studio-form">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Full name</Label><Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="cs-name" /></div>
                <div><Label>Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cs-email" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Phone</Label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cs-phone" /></div>
                <div><Label>Quantity</Label><Input required type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="cs-qty" /></div>
              </div>
              <div><Label>Shipping address</Label><Textarea required rows={2} value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} data-testid="cs-address" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Size</Label>
                  <Select value={form.size} onValueChange={(v) => setForm({ ...form, size: v })}>
                    <SelectTrigger data-testid="cs-size"><SelectValue /></SelectTrigger>
                    <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Color</Label><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="cs-color" /></div>
              </div>
              <div><Label>Special instructions (optional)</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cs-notes" /></div>
              <label className="flex items-start gap-2 text-sm text-navy/75">
                <input type="checkbox" checked={form.rights_confirmed} onChange={(e) => setForm({ ...form, rights_confirmed: e.target.checked })} className="mt-1 accent-navy-royal" data-testid="cs-rights" />
                I confirm I have the rights to use any images I uploaded for this design.
              </label>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} disabled={submitting} className="flex-1 rounded-md" data-testid="cs-back">Keep Editing</Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-navy-dark hover:bg-navy-royal text-white rounded-md uppercase tracking-[0.15em] text-xs font-bold" data-testid="cs-submit">{submitting ? "Submitting…" : "Submit Order"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
